import { type ReactElement } from 'react';
import type { LiveState } from '@/hooks/use-venue-socket';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const TONE: Readonly<Record<LiveState, string>> = {
  live: 'border-primary/30 bg-primary/10 text-primary',
  connecting: 'border-amber-300 bg-amber-50 text-amber-800',
  offline: 'border-red-300 bg-red-50 text-red-700',
};

const DOT: Readonly<Record<LiveState, string>> = {
  live: 'bg-emerald-500 animate-pulse',
  connecting: 'bg-amber-500',
  offline: 'bg-red-500',
};

const COPY: Readonly<Record<LiveState, CopyKey>> = {
  live: 'live',
  connecting: 'connecting',
  offline: 'offline',
};

// Connection health, where the floor can see it without leaving the map. §16
export function LiveIndicator({ state }: { state: LiveState }): ReactElement {
  const { t } = useTranslation();
  return (
    <span
      data-testid="live"
      data-state={state}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold',
        TONE[state],
      )}
    >
      <i aria-hidden className={cn('size-1.5 rounded-full', DOT[state])} />
      {t(COPY[state])}
    </span>
  );
}
